import { Component, OnInit, ViewChild } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { MatDialog, MatSnackBar } from "@angular/material";
import { DatatableComponent } from "@swimlane/ngx-datatable";

import { FuseNavigationService } from "../../../core/components/navigation/navigation.service";
import { FuseTranslationLoaderService } from "../../../core/services/translation-loader.service";
import { ErrorResponse } from "../../../data/model/errorResponse";
import { Organisation } from "../../../data/model/organisation";
import { User } from "../../../data/model/user";
import { OrganisationsDataManager } from "../../../data/organisations.datamanager";
import { UsersDataManager } from "../../../data/users.datamanager";
import { ConfirmDialogComponent } from "../dialogs/confirm/confirm.dialog.component";

import { AddUserDialogComponent } from "./add/add.user.dialog.component";
import { EditUserDialogComponent } from "./edit/edit.user.dialog.component";
import { locale as english } from "./i18n/en";
import { locale as dutch } from "./i18n/nl";
import { ImportUsersDialogComponent } from "./import/import.users.dialog.component";
import { ExportUsersDialogComponent } from "./export/export.users.dialog.component";
import { AdvancedFilteringByFileUsersDialogComponent } from "./advanced.filtering/advanced.filtering.by.file.users.dialog.component";
import { PromoteUserDialogComponent } from "./promote/promote.user.dialog.component";

@Component({
    selector: "fuse-users",
    templateUrl: "./users.component.html",
    styleUrls: ["./users.component.scss"]
})
export class UsersComponent implements OnInit {
    @ViewChild(DatatableComponent) public table: DatatableComponent;
    public currentPageLimit: number = 10;
    public currentPageOffset: number = 0;
    public readonly pageLimitOptions = [{ value: 5 }, { value: 10 }, { value: 25 }, { value: 50 }, { value: 100 }];
    public selectedRows: any[] = [];

    rows: User[];
    licenses = 0;
    name: string;
    userAmount = 0;
    role: string;
    userlist: User[];
    tempUsers: User[];
    temp = [];
    organisationlist: Organisation[];
    selectedOrganisation: Organisation = null;
    filter: string;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private navigationService: FuseNavigationService,
        private usersDataManager: UsersDataManager,
        private organisationsDataManager: OrganisationsDataManager,
        private dialog: MatDialog,
        public snackBar: MatSnackBar,
        private translationLoader: FuseTranslationLoaderService
    ) {
        this.translationLoader.loadTranslations(english, dutch);

        this.route.queryParams.subscribe((params) => {
            if (this.filter !== params.filter) {
                this.filter = params.filter;
                this.ngOnInit();
            }
        });
    }

    ngOnInit() {
        this.role = localStorage.getItem("role");

        if (this.role === "superadmin") {
            this.organisationsDataManager.getAllOrganisations().subscribe(
                (response) => {
                    this.organisationlist = response;
                    this.usersDataManager.getAllUsers(this.filter).subscribe(
                        (res) => {
                            this.userlist = res;
                            for (let i = 0; i < this.userlist.length; i++) {
                                const user: User = this.userlist[i];
                                if (user.organisation !== undefined) {
                                    user.organisationObject = this.getOrganisationById(user.organisation);
                                    if (i === this.userlist.length - 1) {
                                        this.rows = this.userlist;
                                    }
                                }
                            }
                            this.setFiltersOnInit();
                        },
                        (err) => {
                            this.showError(err.json());
                            this.setFiltersOnInit();
                        }
                    );
                },
                (err) => {
                    this.showError(err.json());
                    this.setFiltersOnInit();
                }
            );
        } else {
            this.usersDataManager.getAllUsers(this.filter).subscribe(
                (res) => {
                    this.userlist = res;
                    this.userAmount = this.userlist.length;
                    this.organisationsDataManager.getOrganisationById(this.userlist[0].organisation).subscribe(
                        (response) => {
                            this.licenses = response.number_of_licences;
                            for (let i = 0; i < this.userlist.length; i++) {
                                this.userlist[i].organisationObject = response;
                                if (i === this.userlist.length - 1) {
                                    this.rows = this.userlist;
                                }
                            }
                            this.setFiltersOnInit();
                        },
                        (err) => {
                            this.showError(err.json());
                            this.setFiltersOnInit();
                        }
                    );
                },
                (err) => {
                    this.showError(err.json());
                    this.setFiltersOnInit();
                }
            );
        }
    }

    setFiltersOnInit() {
        this.route.queryParams.subscribe((params) => {
            console.log(params);

            if (params.organisation) {
                this.selectedOrganisation = this.getOrganisationById(params.organisation);
                this.organisationFilterChange();
            }

            if (params.search) {
                this.name = params.search;
                this.updateSearch({ target: { value: params.search } });
            }

            if (params.per_page) {
                this.onLimitChange(params.per_page);
            }

            if (params.page) {
                this.currentPageOffset = params.page - 1;
            }
        });
    }
    //
    onRowSelect({ selected }) {
        console.log("onRowSelect");
        // console.log(event)
        this.selectedRows = selected;
        // this.selectedRows.push(...selected);
        console.log(this.selectedRows);
    }

    organisationFilterChange() {
        this.name = "";

        if (!this.selectedOrganisation) {
            this.rows = this.userlist;
            this.router.navigate([], {
                queryParams: { organisation: undefined },
                queryParamsHandling: "merge"
            });
            return;
        }

        this.tempUsers = [];
        this.userlist.forEach((user) => {
            if (user.organisation === this.selectedOrganisation._id) {
                this.tempUsers.push(user);
            }
        });
        this.rows = this.tempUsers;
        this.router.navigate([], {
            queryParams: { organisation: this.selectedOrganisation._id },
            queryParamsHandling: "merge"
        });
    }

    getOrganisationById(id: String): Organisation {
        let org = null;
        this.organisationlist.forEach((organisation) => {
            if (organisation._id === id) {
                org = organisation;
            }
        });
        return org;
    }

    deleteSelectedUsers() {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            height: "170px",
            width: "300px",
            data: {
                dialogTitle: "Delete all " + this.selectedRows.length + " users",
                accept: "true"
            }
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result !== null && result !== undefined) {
                if (result === "true") {
                    this.usersDataManager.deleteUsers(this.selectedRows.map((row) => row._id)).subscribe(
                        (response) => {
                            this.showMessage("Users deleted");
                            this.ngOnInit();
                        },
                        (err) => {
                            this.showError(err.json());
                        }
                    );
                }
            }
        });
    }

    removeUser(userId) {
        this.usersDataManager.getLoggedInUser().subscribe(
            (res) => {
                if (res._id === userId) {
                    this.showMessage("Cant remove yourself");
                } else {
                    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
                        height: "170px",
                        width: "300px",
                        data: { dialogTitle: "Delete user?", accept: "true" }
                    });

                    dialogRef.afterClosed().subscribe((result) => {
                        if (result !== null && result !== undefined) {
                            if (result === "true") {
                                this.usersDataManager.deleteUser(userId).subscribe(
                                    (response) => {
                                        this.showMessage("User deleted");
                                        this.ngOnInit();
                                    },
                                    (err) => {
                                        this.showError(err.json());
                                    }
                                );
                            }
                        }
                    });
                }
            },
            (err) => {
                this.showError(err.json());
            }
        );
    }

    deleteSelectedUsersTrainingProgress() {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            height: "170px",
            width: "380px",
            data: {
                dialogTitle: `Delete all user progresses for ${this.selectedRows.length} users`,
                accept: "true"
            }
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result !== null && result !== undefined) {
                if (result === "true") {
                    this.usersDataManager.deleteTrainingProgressByUsers(this.selectedRows.map((row) => row._id)).subscribe(
                        (response) => {
                            this.showMessage("User progresses deleted");
                            this.ngOnInit();
                        },
                        (err) => {
                            this.showError(err.json());
                        }
                    );
                }
            }
        });
    }

    deleteTrainingProgressByUser(userId) {
        this.usersDataManager.getLoggedInUser().subscribe(
            (res) => {
                const dialogRef = this.dialog.open(ConfirmDialogComponent, {
                    height: "170px",
                    width: "300px",
                    data: {
                        dialogTitle: "Delete user progress?",
                        accept: "true"
                    }
                });

                dialogRef.afterClosed().subscribe((result) => {
                    if (result !== null && result !== undefined) {
                        if (result === "true") {
                            this.usersDataManager.deleteTrainingProgressByUser(userId).subscribe(
                                (response) => {
                                    this.showMessage("User progress deleted");
                                    this.ngOnInit();
                                },
                                (err) => {
                                    this.showError(err.json());
                                }
                            );
                        }
                    }
                });
            },
            (err) => {
                this.showError(err.json());
            }
        );
    }

    getUserById(userId: string): User {
        let user = null;
        this.userlist.forEach((us) => {
            if (us._id === userId) {
                user = us;
            }
        });
        return user;
    }

    promoteUser(userId) {
        this.usersDataManager.getLoggedInUser().subscribe(
            (res) => {
                if (res._id === userId) {
                    this.showMessage("Cant promote yourself");
                } else {
                    const user: User = this.getUserById(userId);
                    const dialogRef = this.dialog.open(PromoteUserDialogComponent, {
                        height: "170px",
                        width: "300px",
                        data: {
                            userName: user.name,
                            role: user.role,
                            userRole: localStorage.getItem("role")
                        }
                    });

                    dialogRef.afterClosed().subscribe((result) => {
                        if (result !== null && result !== undefined) {
                            if (result.role !== null && result.role !== undefined) {
                                this.usersDataManager.updateUserRole(userId, result.role).subscribe(
                                    (response) => {
                                        this.showMessage("user edit successful");
                                        this.ngOnInit();
                                    },
                                    (err) => {
                                        this.showError(err.json());
                                    }
                                );
                            }
                        }
                    });
                }
            },
            (err) => {
                this.showError(err.json());
            }
        );
    }

    advancedDialogButtonClick(type) {
        let dialogComponent;
        if (type === "IMPORT") {
            dialogComponent = ImportUsersDialogComponent;
        } else if (type === "EXPORT") {
            dialogComponent = ExportUsersDialogComponent;
        } else if (type === "FILTER") {
            dialogComponent = AdvancedFilteringByFileUsersDialogComponent;
        }

        this.role = localStorage.getItem("role");
        if (this.role === "superadmin") {
            this.organisationsDataManager.getAllOrganisations().subscribe(
                (response) => {
                    const dialogRef = this.dialog.open(dialogComponent, {
                        height: "310px",
                        width: "400px",
                        data: {
                            organisationList: response,
                            selectedOrganisation: null
                        }
                    });

                    dialogRef.afterClosed().subscribe((result) => {
                        if (result !== null && result !== undefined) {
                            if (typeof result === "string") {
                                this.showMessage(result);
                                this.ngOnInit();
                            } else if (type === "FILTER") {
                                this.router.navigate([], {
                                    queryParams: { filter: result._id },
                                    queryParamsHandling: "merge"
                                });
                            } else {
                                this.showError(result);
                            }
                        }
                    });
                },
                (err) => {
                    this.showError(err.json());
                }
            );
        } else {
            this.usersDataManager.getLoggedInUser().subscribe(
                (res) => {
                    const dialogRef = this.dialog.open(dialogComponent, {
                        height: "230px",
                        width: "400px",
                        data: {
                            organisationList: null,
                            selectedOrganisation: res.organisation
                        }
                    });

                    dialogRef.afterClosed().subscribe((result) => {
                        if (result !== null && result !== undefined) {
                            if (typeof result === "string") {
                                this.showMessage(result);
                                this.ngOnInit();
                            } else if (type === "FILTER") {
                                this.router.navigate([], {
                                    queryParams: { filter: result._id },
                                    queryParamsHandling: "merge"
                                });
                            } else {
                                this.showError(result);
                            }
                        }
                    });
                },
                (err) => {
                    this.showError(err.json());
                }
            );
        }
    }

    addUserButtonClick() {
        const dialogRef = this.dialog.open(AddUserDialogComponent, {
            height: "450px",
            width: "400px",
            data: {
                organisations: this.organisationlist,
                role: localStorage.getItem("role"),
                selectedRole: "appuser"
            }
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result !== null && result !== undefined) {
                if (result.userName !== null && result.userName !== undefined && result.userEmail !== null && result.userEmail !== undefined) {
                    let organisationId = "";
                    if (localStorage.getItem("role") === "superadmin") {
                        organisationId = result.organisation._id;
                    } else {
                        organisationId = null;
                    }
                    const userRole = result.selectedRole;
                    this.usersDataManager.createUser(result.userName, result.userEmail, userRole, organisationId, result.log_requests).subscribe(
                        (response) => {
                            this.showMessage("user is created");
                            this.ngOnInit();
                        },
                        (err) => {
                            this.showError(err.json());
                        }
                    );
                }
            }
        });
    }

    showLogs(userId) {
        this.router.navigate([userId + "/log.entries"], {
            relativeTo: this.route
        });
    }

    editUser(userId) {
        this.usersDataManager.getLoggedInUser().subscribe(
            (res) => {
                if (res._id === userId) {
                    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
                        height: "200px",
                        width: "400px",
                        data: {
                            dialogTitle: "You are about to edit yourself",
                            dialigText: " Are you sure you want to do this?",
                            accept: "true"
                        }
                    });

                    dialogRef.afterClosed().subscribe((result) => {
                        if (result !== null && result !== undefined) {
                            if (result === "true") {
                                this.showEditScreen(userId);
                            } else {
                                this.dialog.closeAll();
                            }
                        }
                    });
                } else {
                    this.showEditScreen(userId);
                }
            },
            (err) => {
                this.showError(err.json());
            }
        );
    }

    showEditScreen(userId) {
        this.usersDataManager.getUserById(userId).subscribe(
            (res) => {
                const dialogRef = this.dialog.open(EditUserDialogComponent, {
                    height: "450px",
                    width: "400px",
                    data: {
                        response: res,
                        userRole: localStorage.getItem("role")
                    }
                });

                dialogRef.afterClosed().subscribe((result) => {
                    if (result !== null && result !== undefined) {
                        if (result.name !== null && result.name !== undefined && result.email !== null && result.email !== undefined) {
                            this.usersDataManager.updateUser(userId, result).subscribe(
                                (resp) => {
                                    this.showMessage("user edit successful");
                                    this.ngOnInit();
                                },
                                (err) => {
                                    this.showError(err.json());
                                }
                            );
                        }
                    }
                });
            },
            (err) => {
                this.showError(err.json());
            }
        );
    }

    public onLimitChange(limit: any): void {
        this.changePageLimit(limit);
        this.table.limit = this.currentPageLimit;
        this.table.recalculate();
        setTimeout(() => {
            if (this.table.bodyComponent.temp.length <= 0) {
                this.table.offset = Math.floor((this.table.rowCount - 1) / this.table.limit);
            }
        });
    }

    private changePageLimit(limit: any): void {
        this.currentPageLimit = parseInt(limit, 10);
        this.router.navigate([], {
            queryParams: { per_page: this.currentPageLimit },
            queryParamsHandling: "merge"
        });
    }

    onDownloadExampleButtonClick() {
        window.open("assets/User-import-example.xlsx");
    }

    showMessage(message: string) {
        this.snackBar.open(message, null, { duration: 5000 });
    }

    showError(message: ErrorResponse) {
        if (message.errors !== null && message.errors !== undefined && message.errors.length > 0) {
            this.snackBar.open(message.errors[0].message, null, {
                duration: 10000
            });
        } else {
            this.snackBar.open("Unknown error occured", null, {
                duration: 10000
            });
        }
    }

    updateSearch(event) {
        const val = event.target.value.toLowerCase();

        // filter our data
        const temp = this.rows.filter(function (d) {
            return d.name.toLowerCase().indexOf(val) !== -1 || d.email.toLowerCase().indexOf(val) !== -1 || !val;
        });

        // update the rows
        this.rows = temp;

        this.router.navigate([], {
            queryParams: { search: val },
            queryParamsHandling: "merge"
        });
    }

    setPage(event) {
        this.currentPageOffset = event.offset;
        this.router.navigate([], {
            queryParams: { page: event.offset + 1 },
            queryParamsHandling: "merge"
        });
    }
}



// WEBPACK FOOTER //
// /tmp/build_f0fbff5b/src/app/main/content/users/users.component.ts